import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'

import { TrendChart, Sparkline } from './Charts.jsx'
import { Headshot, TeamMark } from './Marks.jsx'
import SeasonHead, { ViewFrame } from './SeasonHead.jsx'
import { seriesColor, teamStyle, toneStyle } from './colors.js'
import { STAT, PICKS } from './historyFormat.js'
import { comparisonSeason, useHead, useHistoryResource, useSeasonChoice, useTeams } from './historyStore.jsx'
import { validPersonId } from './history.js'
import { listPath } from './paths.js'
import { playerModel, stintsOf, trendModel } from './playerModel.js'
import { LOADING, READY, UNREAD } from './poll.js'
import { useBack } from './useBack.js'
import { useDocumentTitle } from '../../useDocumentTitle.js'

// A player's page. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// FOUR STATES FROM ONE ROUTE, AND NONE OF THEM HAS AN EMPTY PLACE IN IT
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's four player states are all this route:
//
//   a full season       his file, the comparison with the other season, the game-by-game chart, percentiles,
//                       splits, totals (Luka Doncic, 2025-26)
//   a traded player     the same, with his hero washed from his first team to his last and a stints card; with no
//                       game log (the backfill stores logs for the newest season only) there is no chart (2024-25)
//   a rookie            one season: no comparison anywhere on the page, and the other season's button dimmed
//   held only in the index   his file is not there and the index has him: the hero and his league ranks, nothing else
//
// `playerModel.js` decides each part's presence, as data; this file draws what it hands back and draws no heading for
// a part that is not there.
//
// ═══════════════════════════════════════════════════════════════════════════
// IT WAITS FOR EVERYTHING IT ASKED FOR, THEN DRAWS ONCE
// ═══════════════════════════════════════════════════════════════════════════
//
// The page asks for his file, the other season's file (the comparison), the leaders file (for the minimum games and his
// league ranks) and, only when his file is not there, the index (for his name and team). The hero's chips and the
// "Not qualified · 50 of 58 games" sentences come from the leaders file, so drawing before it answers would add them a
// moment later and move everything below. Nothing is drawn but the head until all four have answered, and a request
// that failed has answered.
//
// NO FILE AND NO INDEX ENTRY IS A SENTENCE ONLY WHEN THE INDEX ANSWERED. A player id the index does not list is
// "no one with that id". Every read having failed is an unreadable feed, and an unreadable feed is the head and
// nothing else (HP5): the page does not know whether he exists.

function Stat({ cell, large }) {
  return (
    <div className="d2d-hoops__stat" data-large={large ? 'true' : 'false'} data-testid={`hoops-stat-${cell.key}`}>
      <div className="d2d-hoops__statvalue">{cell.value}</div>
      <div className="d2d-hoops__statlabel d2d-micro">{cell.label}</div>
      {cell.comparison ? (
        <div className="d2d-hoops__statcmp" data-testid={`hoops-stat-${cell.key}-cmp`}>
          <div>{cell.comparison}</div>
          <div className="d2d-hoops__statdelta">{cell.delta}</div>
        </div>
      ) : null}
    </div>
  )
}

function Hero({ model, personId, teams, stale }) {
  const first = teams.byId(model.teamIds[0])
  const lastId = model.teamIds[model.teamIds.length - 1]
  // One team washes from the left only; a player who ends where he began is one team too.
  const last = lastId !== model.teamIds[0] ? teams.byId(lastId) : null
  return (
    <section
      className="d2d-hoops__phero"
      aria-label={model.name}
      style={toneStyle(first, last, { faded: stale })}
      data-testid="hoops-player-hero"
    >
      <div className="d2d-hoops__pheroid">
        <Headshot player={{ person_id: Number(personId), name: model.name }} which="away" size="large" />
        <div className="d2d-hoops__pheroname">
          <h1 className="d2d-hoops__h1" data-testid="hoops-player-name">
            {model.name}
          </h1>
          <div className="d2d-hoops__pmeta d2d-micro" data-testid="hoops-player-meta">
            {model.meta}
          </div>
        </div>
      </div>
      {model.big.length > 0 ? (
        <div className="d2d-hoops__bigstats" data-testid="hoops-player-big">
          {model.big.map((cell) => (
            <Stat key={cell.key} cell={cell} large />
          ))}
        </div>
      ) : null}
      {model.small.length > 0 ? (
        <div className="d2d-hoops__smallstats" data-testid="hoops-player-small">
          {model.small.map((cell) => (
            <Stat key={cell.key} cell={cell} large={false} />
          ))}
        </div>
      ) : null}
      <div className="d2d-hoops__chips" data-testid="hoops-player-chips">
        {model.gpLine ? <span className="d2d-hoops__gpline">{model.gpLine}</span> : null}
        {model.chips.map((chip) => (
          <span className="d2d-hoops__chip d2d-micro" key={chip}>
            {chip}
          </span>
        ))}
      </div>
    </section>
  )
}

/** The game-by-game card, which owns its own picked stat and selected game and starts over for another player or season. */
function TrendCard({ file, teams }) {
  const [stat, setStat] = useState('pts')
  const [picked, setPicked] = useState({})
  const stints = stintsOf(file)
  const teamAt = useCallback(
    (date) => (stints?.find((stint) => date >= stint.from && date <= stint.to) ?? { abbr: file.team }).abbr,
    [stints, file.team],
  )
  const model = trendModel({ file, stat, selected: picked[stat] ?? null, teamAt })
  if (model === null) return null
  return (
    <TrendChart
      model={model}
      picks={PICKS.map((key) => ({ key, label: STAT[key] }))}
      teams={teams}
      onStat={setStat}
      onSelect={(index) => setPicked((previous) => ({ ...previous, [model.key]: index }))}
    />
  )
}

/** Where he stands in the league, with his last ten games: for a player held only in the index. */
function RanksCard({ model, teams }) {
  if (model.rankRows.length === 0) return null
  const team = teams.byId(model.teamIds.at(-1))
  return (
    <section
      className="d2d-hoops__panel d2d-hoops__panel--flush"
      aria-label="League ranks"
      style={teamStyle(team)}
      data-testid="hoops-player-ranks"
    >
      <div className="d2d-hoops__panelhead">
        <span className="d2d-hoops__paneltitle d2d-micro">League ranks</span>
        <span className="d2d-hoops__panelnote d2d-micro">Last 10</span>
      </div>
      {model.rankRows.map((row) => (
        <div className="d2d-hoops__rankrow" key={row.cat} data-testid="hoops-player-rank">
          <span className="d2d-hoops__ranktext">
            <span className="d2d-hoops__rankname">{row.label}</span>
            <span className="d2d-hoops__ranksub">{row.sub}</span>
          </span>
          <Sparkline spark={row.spark} />
          <span className="d2d-hoops__rankvalue">{row.value}</span>
        </div>
      ))}
    </section>
  )
}

function TotalsCard({ model }) {
  if (model.totals.length === 0) return null
  return (
    <section className="d2d-hoops__panel" aria-label="Season totals" data-testid="hoops-player-totals">
      <span className="d2d-hoops__paneltitle d2d-micro">Season totals</span>
      <div className="d2d-hoops__totals">
        {model.totals.map((total) => (
          <div key={total.key}>
            <div className="d2d-hoops__totalvalue">{total.value}</div>
            <div className="d2d-hoops__totallabel d2d-micro">{total.label}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

function PercentilesCard({ model, teams }) {
  if (model.percentiles.length === 0) return null
  const team = teams.byId(model.teamIds.at(-1))
  return (
    <section
      className="d2d-hoops__panel d2d-hoops__panel--flush"
      aria-label="Percentiles"
      style={teamStyle(team)}
      data-testid="hoops-player-pct"
    >
      <span className="d2d-hoops__paneltitle d2d-micro">Percentile vs qualified</span>
      <p className="d2d-hoops__panelnote2">
        Per game, against everyone who met the minimum. Further right means more of it, turnovers included.
      </p>
      <div className="d2d-hoops__pctscale d2d-micro" aria-hidden="true">
        <span />
        <span className="d2d-hoops__pctticks">
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </span>
        <span className="d2d-hoops__pctunit">PCTL</span>
      </div>
      {model.percentiles.map((row) => (
        <div className="d2d-hoops__pctrow" key={row.key} data-qualified={row.qualified ? 'true' : 'false'} data-testid={`hoops-pct-${row.key}`}>
          <div className="d2d-hoops__pctname">
            <div className="d2d-hoops__pctlabel d2d-micro">{row.label}</div>
            <div className="d2d-hoops__pctvalue d2d-micro">{row.value}</div>
          </div>
          {row.qualified ? (
            <>
              <div className="d2d-hoops__pctbar" aria-hidden="true">
                <span className="d2d-hoops__pctfill" style={{ width: `${row.width}%` }} />
                <span className="d2d-hoops__pctmedian" />
              </div>
              <div className="d2d-hoops__pctrank">
                <div className="d2d-hoops__pctnumber">{row.percentile}</div>
                <div className="d2d-hoops__pctpool d2d-micro">of {row.pool}</div>
              </div>
            </>
          ) : (
            <div className="d2d-hoops__pctwhy" data-testid="hoops-pct-why">
              {row.why}
            </div>
          )}
        </div>
      ))}
    </section>
  )
}

function SplitsCard({ model }) {
  const splits = model.splits
  if (splits === null) return null
  return (
    <section
      className="d2d-hoops__panel d2d-hoops__panel--flush"
      aria-label="Splits"
      data-testid="hoops-player-splits"
      style={{ '--hoops-splitcols': splits.columns }}
    >
      <span className="d2d-hoops__paneltitle d2d-micro">{splits.title}</span>
      <div className="d2d-hoops__splitrow d2d-hoops__splitrow--head" aria-hidden="true">
        <span />
        {splits.heads.map((head) => (
          <span className="d2d-hoops__splithead d2d-micro" key={head}>
            {head}
          </span>
        ))}
      </div>
      {splits.rows.map((row) => (
        <div className="d2d-hoops__splitrow" key={row.label}>
          <span className="d2d-hoops__splitlabel d2d-micro">{row.label}</span>
          {row.cells.map((cell, index) => (
            <span className="d2d-hoops__splitcell" key={splits.heads[index]}>
              {cell}
            </span>
          ))}
        </div>
      ))}
      {splits.note ? (
        <p className="d2d-hoops__panelnote2" data-testid="hoops-player-neutral">
          {splits.note}
        </p>
      ) : null}
    </section>
  )
}

function StintsCard({ model, teams }) {
  const stints = model.stints
  if (stints === null) return null
  return (
    <section className="d2d-hoops__panel" aria-label="Stints" data-testid="hoops-player-stints">
      <div className="d2d-hoops__panelhead">
        <span className="d2d-hoops__paneltitle d2d-micro">Traded mid-season</span>
        <span className="d2d-hoops__panelnote d2d-micro">{stints.count}</span>
      </div>
      <div className="d2d-hoops__stintbar" aria-hidden="true">
        {stints.segments.map((segment) => (
          <span
            key={segment.teamId + segment.left}
            className="d2d-hoops__stintseg"
            style={{ left: `${segment.left}%`, width: `${segment.width}%`, '--hoops-line': seriesColor(teams.byId(segment.teamId)) ?? undefined }}
          />
        ))}
      </div>
      <div className="d2d-hoops__stintends d2d-micro" aria-hidden="true">
        <span>{stints.start}</span>
        <span>{stints.end}</span>
      </div>
      <div className="d2d-hoops__stintlist">
        {stints.rows.map((row) => (
          <div className="d2d-hoops__stint" key={row.n} style={teamStyle(teams.byId(row.teamId))} data-testid="hoops-player-stint">
            <span className="d2d-hoops__stintn d2d-micro">{row.n}</span>
            <TeamMark team={teams.byId(row.teamId)} tricode={row.abbr} which="away" />
            <span className="d2d-hoops__stinttext">
              <span className="d2d-hoops__stintname">{row.name}</span>
              <span className="d2d-hoops__stintrange">{row.range}</span>
            </span>
            <span className="d2d-hoops__stintgp">
              <span className="d2d-hoops__stintgpvalue">{row.gp}</span>
              <span className="d2d-hoops__stintgplabel d2d-micro">GP</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

function PlayerBody({ personId }) {
  const choice = useSeasonChoice()
  const teams = useTeams()
  const season = choice.selected?.season ?? null
  const other = comparisonSeason(choice.list, season)
  const file = useHistoryResource('player', season, personId)
  const otherFile = useHistoryResource('player', other?.season ?? null, personId)
  const leaders = useHistoryResource('leaders', season)
  // The index is asked for only when his file is not there: it is where his name and team come from then.
  const needsIndex = file.state === UNREAD
  const index = useHistoryResource('index', needsIndex ? season : null)
  const back = useBack('Players', listPath('players', season))

  const waiting =
    file.state === LOADING ||
    leaders.state === LOADING ||
    (other !== null && otherFile.state === LOADING) ||
    (needsIndex && index.state === LOADING)
  const entry =
    index.state === READY ? (index.data.players.find((player) => player.id === Number(personId)) ?? null) : null
  const model = waiting
    ? null
    : playerModel({
        season,
        personId: Number(personId),
        file: file.state === READY ? file.data : null,
        other: other !== null && otherFile.state === READY ? { season: other.season, file: otherFile.data } : null,
        entry,
        leaders: leaders.state === READY ? leaders.data : null,
        nameFor: (stint) => teams.nameOf(stint.team_id, stint.abbr),
      })
  const head = useHead({
    season,
    entry: choice.selected,
    asOf: model?.asOf,
    source: model?.source,
    ready: model !== null,
  })
  useDocumentTitle(model?.name ?? 'Player')

  if (choice.selected === null) return null

  // The season the comparison asked about and found nothing in, which is dimmed: its button cannot lead anywhere.
  const unavailable = new Set()
  if (other !== null && otherFile.state === UNREAD) unavailable.add(other.season)

  const noRecord = !waiting && model === null && index.state === READY

  return (
    <div className="d2d-hoops__view" data-testid="hoops-player" data-stale={head.stale ? 'true' : 'false'} data-person-id={personId}>
      <SeasonHead choice={choice} unavailable={unavailable} head={head} title={null} back={back} />
      {noRecord ? (
        <p className="d2d-hoops__none" data-testid="hoops-player-none">
          No one with that id is in the {season} index.
        </p>
      ) : null}
      {model === null ? null : (
        <div className="d2d-hoops__pagecols" data-testid="hoops-player-cols">
          <div className="d2d-hoops__pagecol">
            <Hero model={model} personId={personId} teams={teams} stale={head.stale} />
            {file.state === READY ? <TrendCard key={`${personId}:${season}`} file={file.data} teams={teams} /> : null}
            <RanksCard model={model} teams={teams} />
            <TotalsCard model={model} />
          </div>
          <div className="d2d-hoops__pagecol">
            <PercentilesCard model={model} teams={teams} />
            <SplitsCard model={model} />
            <StintsCard model={model} teams={teams} />
            {model.bio ? (
              <p className="d2d-hoops__bio" data-testid="hoops-player-bio">
                {model.bio}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

export default function PlayerView() {
  const { personId } = useParams()
  const valid = validPersonId(personId)
  return (
    <ViewFrame tab="players">
      {valid === null ? (
        <p className="d2d-hoops__none" data-testid="hoops-player-invalid">
          That is not a player id.
        </p>
      ) : (
        // Keyed by the id, so following a link from one player to another starts over: the picked stat, the
        // selected game and everything else that is local state belongs to the player it was picked on.
        <PlayerBody key={valid} personId={valid} />
      )}
    </ViewFrame>
  )
}
